import Axios from 'axios';
import * as Routes from '@/modules/routes';
import serializeParams from '@/utils/serializeParams';
import type { RansackSearch } from '@/components/admin/SysadminSPA/shared/filterTypes';
import useProgressiveLoad from '@/components/admin/SysadminSPA/shared/useProgressiveLoad';
import QueryKeys from '@/utils/QueryKeys';

export interface ReferenceMaterial {
  id: number;
  title: string;
  primaryImage: string | null;
  questionsCount: number;
  programSummary: string;
  nonStandardsTags: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RawReferenceMaterial {
  id: number;
  title: string;
  primary_image: string | null;
  questions_count: number;
  program_summary: string;
  non_standards_tags: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

const FETCH_PAGE_SIZE = 100;

const fetchPage = async (page: number, search: RansackSearch) => {
  const params: Record<string, unknown> = {
    extra_attributes: ['non_standards_tags'],
    includes: ['tags', 'programs', 'images', 'question_groups'],
    only: [
      'id', 'title', 'primary_image', 'questions_count', 'program_summary',
      'published', 'created_at', 'updated_at',
    ],
    page,
    per_page: FETCH_PAGE_SIZE,
  };

  if (Object.keys(search).length > 0) {
    params.search = search;
  }

  const basePath = Routes.plato_api_reference_materials_path();
  const queryString = serializeParams(params);
  const url = queryString ? `${basePath}?${queryString}` : basePath;

  const response = await Axios.get(url);

  return response.data;
};

const transform = (raw: RawReferenceMaterial): ReferenceMaterial => ({
  createdAt: raw.created_at,
  id: raw.id,
  nonStandardsTags: raw.non_standards_tags,
  primaryImage: raw.primary_image,
  programSummary: raw.program_summary,
  published: raw.published,
  questionsCount: raw.questions_count,
  title: raw.title,
  updatedAt: raw.updated_at,
});

// AIDEV-NOTE: Progressive-loading hook for the Reference Materials table.
// Thin wrapper over useProgressiveLoad with table-specific fetch/transform logic.
const useContentReferenceMaterials = (search: RansackSearch = {}) => (
  useProgressiveLoad<RawReferenceMaterial, ReferenceMaterial>({
    fetchPage: page => fetchPage(page, search),
    queryKey: [QueryKeys.ContentReferenceMaterials, search],
    transform,
  })
);

export default useContentReferenceMaterials;
